import { ChangeDetectionStrategy, Component, Inject, Input, OnInit } from '@angular/core';
import { ECVideoService } from '../../shared/ec-modal/ec-video.service';
import { MediaPlayerService } from '../../shared/media-player/media-player.service';
import { StudyBundleCourseDto } from '../../shared/types/StudyBundleCourseDto';
import { WindowService } from '../../shared/window.service';
import { ActivityHistoryDetail } from '../activity-calendar-service/activity-history-detail';
import { ActivityType } from './activity-type';
import { ActivitySource } from './activity-source';
import { SpeechaceService } from '../../shared/speechace-modal/speechace.service';
import { IndustryContentService } from '../../shared/industry-content.service';
import { StudyBundleCourseStatusType } from '../../shared/types/StudyBundleCourseStatusType';
import QueryParamsUtils from '../../shared/helpers/query-params-utils';
import { serializeParams } from '../../shared/helpers/serialize-params';
import { BehaviorSubject } from 'rxjs';
import { CourseDataService } from '../../shared/course-data.service';

@Component({
  selector: 'lp2-activity-snippet',
  templateUrl: './activity-snippet.component.html',
  styleUrls: ['./activity-snippet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivitySnippetComponent implements OnInit {
  @Input() activity: ActivityHistoryDetail;

  mediaPlayerService: MediaPlayerService;
  ActivityType = ActivityType;
  imageSource: string;
  studyBundleCourseDto: StudyBundleCourseDto;
  divId = 'player-holder-';
  activityTypesWithReplayLink = [ActivityType.PRACTICE, ActivityType.IDIOMS, ActivityType.NEWS, ActivityType.LESSON];
  displayReplayLink$ = new BehaviorSubject<boolean>(false);
  serializedParams$ = new BehaviorSubject<string>(null);

  public ECLoaded = false;
  public ECError = false;

  constructor(
    @Inject('MediaPlayerService') mediaPlayerService: MediaPlayerService,
    private videoService: ECVideoService,
    private speechaceService: SpeechaceService,
    private industryContentService: IndustryContentService,
    private windowService: WindowService,
    private courseDataService: CourseDataService
  ) {
    this.mediaPlayerService = mediaPlayerService;
    videoService.initialize(
      () => {
        this.ECLoaded = true;
        this.ECError = false;
      },
      () => {
        this.ECError = true;
        this.ECLoaded = false;
      }
    );
  }

  ngOnInit() {
    if (!this.activity) {
      return;
    }

    if (!this.activity.url) {
      this.subscribeToCourseData(this.activity.courseId);
    }

    this.createStudyBundleCourseDto(this.activity);
    this.imageSource = this.getImageSource(this.activity.courseType);
    this.divId = 'player-holder-' + this.activity.courseType + '-' + this.studyBundleCourseDto.id;
  }

  playPracticeActivity() {
    switch (this.activity.sourceId) {
      case ActivitySource.ENGLISH_CENTRAL:
        this.studyBundleCourseDto.contentTypeId = 3;
        this.videoService.open(this.studyBundleCourseDto);
        break;
      case ActivitySource.SPEECHACE:
        this.speechaceService.openActivity(
          this.studyBundleCourseDto.title,
          this.studyBundleCourseDto.id,
          this.studyBundleCourseDto.mediaData,
          this.studyBundleCourseDto.status === StudyBundleCourseStatusType.COMPLETE
        );
        break;
      case ActivitySource.ISC:
        this.industryContentService.openActivity(this.studyBundleCourseDto);
        break;
    }
  }

  replayActivity() {
    if (!this.activity) {
      return;
    }

    switch (this.activity.courseType) {
      case ActivityType.PRACTICE:
        this.playPracticeActivity();
        break;
      case ActivityType.IDIOMS:
      case ActivityType.NEWS:
        if (!this.activity.url) {
          break;
        }
        this.studyBundleCourseDto.contentTypeId = 5;
        this.mediaPlayerService.playMedia(this.studyBundleCourseDto, this.divId, null);
        break;
      case ActivityType.LESSON:
        if (!this.serializedParams$.value) {
          break;
        }
        const params = QueryParamsUtils.hashQueryParams(this.serializedParams$.value);
        this.windowService.redirectToHref(`/learning/lesson.html?q=${params}`);
        break;
    }
  }

  private getImageSource(courseType: string): string {
    const practiceSource = 'practicas';
    const privateClassesSource = 'clases-privadas';
    const liveClassesSource = 'clases-en-vivo';
    const unitsSource = 'unidades';

    let sourceName = '';

    switch (courseType) {
      case ActivityType.PRACTICE.toString():
      case ActivityType.IDIOMS.toString():
      case ActivityType.NEWS.toString():
        sourceName = practiceSource;
        break;
      case ActivityType.LIVE_CLASS.toString():
        sourceName = liveClassesSource;
        break;
      case ActivityType.PRIVATE_CLASS.toString():
        sourceName = privateClassesSource;
        break;
      case ActivityType.LESSON.toString():
      case ActivityType.UNIT_ASSESSMENT.toString():
      case ActivityType.LEVEL_ZERO.toString():
        sourceName = unitsSource;
        break;
    }
    return `../images/activity-registry/${sourceName}.svg`;
  }

  private subscribeToCourseData(courseId: number) {
    this.courseDataService.getCourseData(courseId).subscribe((courseData) => {
      this.serializedParams$.next(
        serializeParams({
          levelName: courseData.level.name,
          unitId: courseData.unit.id,
          unitName: courseData.unit.name,
          courseId: this.activity.courseId,
        })
      );

      this.displayReplayLink$.next(
        this.activity &&
          this.activityTypesWithReplayLink.indexOf(<ActivityType>this.activity.courseType) > -1 &&
          !!this.serializedParams$.value
      );
    });
  }

  private createStudyBundleCourseDto(activity: ActivityHistoryDetail) {
    this.studyBundleCourseDto = {
      contentType: null,
      contentTypeId: null,
      description: activity.courseDesc,
      featured: true,
      id: activity.courseId,
      img: null,
      largeImg: null,
      lessonNumber: null,
      mediaData: activity.mediaData,
      points: 0,
      required: false,
      scheduledHour: null,
      sequence: null,
      sourceId: null,
      start: null,
      status: StudyBundleCourseStatusType.COMPLETE,
      title: activity.courseTitle,
      transcript: activity.transcript,
      type: activity.courseType,
      url: activity.url,
      lessonType: null
    };
  }
}



// WEBPACK FOOTER //
// ./ts/app/activity-calendar/activity-snippet/activity-snippet.component.ts